import { Injectable, Injector, OnDestroy } from '@angular/core';

import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { isEqual } from 'lodash';

import { ChartGroupInputModel } from '../../../Common/Charts/models/chart-group-input.model';
import { ChartModel } from '../../../Common/Charts/models/chart.model';
import { Am5Chart } from '../../../Common/Charts/models/am5-chart.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { SocialSeriesService } from './social-series.service';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
/**
 * Opened by name through BootstrapCoreDialogMap rather than imported: ChartDrillDownDialogComponent
 * extends ChartBlockComponent, which injects this service, so importing it here closes a cycle and
 * leaves whichever subclass of ChartBlockComponent loads first extending undefined.
 */
const chartDrillDownDialogKey: string = 'ChartDrillDownDialogComponent';
import { ReportDateRangeModel } from '@Common/Reports/models/report-date-range.model';
import { ThemeService } from './theme.service';
import { Theme } from '../models/theme.model';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { DateQueryDateTypeEnum } from '@api/mentions/enum/date-query-date-type.enum';


@Injectable()
export class SocialChartDetailsDialogService implements OnDestroy {
    chartDetailsDialogId: string;
    viewMentionsUrl: string;
    searchCriteria: MentionSearchCriteriaModel;
    cachedSearchQueryFilters: MentionSearchCriteriaModel;

    currentTheme: Theme = null;

    subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        public injector: Injector,
        private socialSeriesService: SocialSeriesService,
        private themeService: ThemeService,
    ) {
        this.subscriptions.push(
            this.themeService.currentResource$.pipe(
                filter(x => !!x)
            ).subscribe(theme => {
                this.currentTheme = theme;
            })
        );
    }

    public openDrillDownPage(url: string, chart?: ChartModel | ChartGroupInputModel | BlockModel | Am5Chart): void {
        const filterString = url.split('filters=')?.[1];
        const model = MentionSearchCriteriaModel.fromFilterQueryString(filterString);
        let searchCriteriaModel = model?.search;

        if (!searchCriteriaModel) {
            searchCriteriaModel = new MentionSearchCriteriaModel();
        }

        if (searchCriteriaModel.query.dateQuery.type === DateQueryTypeEnum.RELATIVE
                || searchCriteriaModel.query.dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            searchCriteriaModel.query.dateQuery = this.socialSeriesService.generateFilterSearchCriteria().query.dateQuery;
        }

        if (this.currentTheme?.id) {
            if ((searchCriteriaModel.filterQuery.agent.include?.length) ||
                (isEqual(this.currentTheme?.subscriptionIds.sort(), searchCriteriaModel.query.agent.include.sort()))) {
                searchCriteriaModel.query.agent.include = [];
            }
            searchCriteriaModel.query.theme.include = [this.currentTheme?.id];
        }

        this.viewMentionsUrl = url;

        if (!this.chartDetailsDialogId) {
            const animationTrigger = new BehaviorSubject(
                new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )
            );

            // we want to use filterQuery.dateQuery query when it's a time chart, so it will not be set to the default 24hrs
            const twentyFourHours = DateQueryModel.getLast24HoursQuery();
            twentyFourHours.dateType = DateQueryDateTypeEnum.PUBLISHED;
            const isEqualTo24Hrs = new ReportDateRangeModel().setFromDateQuery(searchCriteriaModel.filterQuery.dateQuery).isEqual(
                new ReportDateRangeModel().setFromDateQuery(twentyFourHours)
            );

            const filterDate = (!isEqualTo24Hrs && searchCriteriaModel.filterQuery.dateQuery) ?
                searchCriteriaModel.filterQuery.dateQuery : searchCriteriaModel.query.dateQuery;

            const ioModel =  {
                inputs: {
                    animationTrigger: animationTrigger,
                    overlayContainer: document.getElementById('main-content'),
                    chart: chart instanceof ChartModel ? chart : undefined,
                    am5Chart: chart instanceof Am5Chart ? chart : undefined,
                    block: chart instanceof BlockModel ? chart : undefined,
                    chartGroup: chart instanceof ChartGroupInputModel ? chart : undefined,
                    baseSearch: searchCriteriaModel,
                    url: url,
                    themeId: this.currentTheme?.id,
                    baseRange: new ReportDateRangeModel().setFromDateQuery(filterDate),
                },
                outputs: {
                    dialogClose: () => {
                        this.chartDetailsDialogId = null;
                    }
                }
            };

            this.chartDetailsDialogId = this.coreDialogService.open(chartDrillDownDialogKey, ioModel, this.injector);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
